import type { AvailabilityDay, DaySlot } from '@concord/domain';
import { type ReactNode, useState } from 'react';
import { cn } from '../cn';
import s from './availability.module.css';

export interface PickedSlot {
  date: string;
  time: string;
}

const key = (p: PickedSlot) => `${p.date} ${p.time}`;

/**
 * Day-by-slot picker (SCH-01): days on the left, half-hour starts on the right, a running
 * count against the maximum (SCH-03), and the zone named on screen (SCH-02). Selecting past
 * the maximum is prevented rather than rejected later.
 */
export function TimePicker({
  days,
  slots,
  selected,
  onChange,
  max,
  zone,
  zoneControl,
}: {
  days: AvailabilityDay[];
  slots: readonly DaySlot[];
  selected: PickedSlot[];
  onChange: (next: PickedSlot[]) => void;
  max: number;
  zone: ReactNode;
  zoneControl?: ReactNode;
}) {
  const [day, setDay] = useState(() => selected[0]?.date ?? days[0]?.date ?? '');
  const chosen = new Set(selected.map(key));
  const full = selected.length >= max;
  const countFor = (d: string) => selected.filter((x) => x.date === d).length;
  const current = days.find((d) => d.date === day);
  const toggle = (time: string) => {
    const p = { date: day, time };
    if (chosen.has(key(p))) onChange(selected.filter((x) => key(x) !== key(p)));
    else if (!full) onChange([...selected, p].sort((a, b) => key(a).localeCompare(key(b))));
  };
  const sections = ['Morning', 'Afternoon', 'Evening'] as const;
  const longDay = current
    ? new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${current.date}T12:00:00Z`))
    : '';

  return (
    <>
      <div className={s.tzbar}>
        <span>{zone}</span>
        {zoneControl}
        <span className={s.count} aria-live="polite">
          {selected.length} <small>of {max} times chosen</small>
        </span>
      </div>

      <div className={s.picker}>
        <div className={s.dayc} role="tablist" aria-label="Days">
          {days.map((d) => {
            const n = countFor(d.date);
            return (
              <button
                key={d.date}
                type="button"
                role="tab"
                aria-selected={d.date === day}
                className={cn(s.dayb, d.date === day && s.on, d.weekend && s.wknd)}
                onClick={() => setDay(d.date)}
              >
                <span className={s.dn}>
                  <b>{d.label}</b>
                  {d.sub && <span>{d.sub}</span>}
                </span>
                {n > 0 && <span className={s.c}>{n}</span>}
              </button>
            );
          })}
        </div>
        <div className={s.slots} role="tabpanel" aria-label={longDay}>
          <div className={s.h}>
            {longDay}
            <span>{countFor(day)} chosen</span>
          </div>
          <div className={s.sgrid}>
            {sections.map((section) => (
              <SlotSection key={section} title={section}>
                {slots
                  .filter((x) => x.section === section)
                  .map((x) => {
                    const on = chosen.has(`${day} ${x.time}`);
                    return (
                      <button
                        key={x.time}
                        type="button"
                        className={cn(s.slot, on && s.on)}
                        aria-pressed={on}
                        disabled={!on && full}
                        onClick={() => toggle(x.time)}
                      >
                        {x.label}
                      </button>
                    );
                  })}
              </SlotSection>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function SlotSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <span className={s.sec}>{title}</span>
      {children}
    </>
  );
}

/** "You have offered": chosen times grouped by day, each removable. */
export function ChosenTimes({
  groups,
  onRemove,
  note,
}: {
  groups: { day: string; items: { key: string; label: string; slot: PickedSlot }[] }[];
  onRemove: (slot: PickedSlot) => void;
  note: ReactNode;
}) {
  return (
    <div className={s.chosen}>
      <span className="key">You have offered</span>
      {groups.map((g) => (
        <div key={g.day} className={s.d}>
          <b>{g.day}</b>
          {g.items.map((i) => (
            <span key={i.key} className={s.chip}>
              {i.label}
              <button
                type="button"
                className={s.x}
                aria-label={`Remove ${g.day} ${i.label}`}
                onClick={() => onRemove(i.slot)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ))}
      <div className={s.req}>
        <span className={s.g} />
        <span>{note}</span>
      </div>
    </div>
  );
}
